/* eslint-disable @typescript-eslint/no-explicit-any */
import { Modal, ModalContent } from '@heroui/react';
import {
  ShoppingCart,
  Check,
  Tag,
  Lock,
  Shield,
  RefreshCw,
  ArrowUpDown,
  CalendarX,
  Gift,
  BarChart3,
  CalendarCheck,
  Users,
  Star,
  Briefcase,
  Sparkles,
  Rocket,
  Crown,
  Zap,
  MessageCircle,
  UserPlus,
  Plus,
  X,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import {
  formatPlanFeatureLabel,
  getAnalyticsValue,
  getFeaturedListingsLimit,
  getMessagingValue,
} from '../../../../utils/membershipPlanUtils';

export interface CheckoutPlan {
  /** Plan tier used by payment APIs (stringified). */
  tier: string;
  /** Stable id for icon mapping (growth | business | free). */
  id: string;
  name: string;
  tagline: string;
  monthly: number;
  yearly: number;
  freeTrialDays?: number;
  limits: {
    services: number;
    teamMembers: number;
    featuredListings: string;
  };
  analyticsLabel: string;
  messaging?: boolean;
  addons?: { label: string; kind?: 'team' | 'service' | 'other' }[];
}

interface CheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: CheckoutPlan | null;
  billing: 'yearly' | 'monthly';
  isLoading?: boolean;
  onConfirm: () => void;
}

const PLAN_TAGLINES: Record<string, string> = {
  'growth builder':
    'Perfect for growing service providers looking to expand their business.',
  'business pro':
    'For established teams that need advanced tools and higher limits.',
};

/** Full colors for color-mix / inline styles (dashboard tokens are HSL components). */
const C = {
  primary: 'hsl(var(--primary))',
  background: 'hsl(var(--background))',
  success: 'hsl(var(--success))',
  foreground: 'hsl(var(--foreground))',
  mutedFg: 'hsl(var(--muted-foreground))',
  border: 'hsl(var(--border))',
  card: 'hsl(var(--card))',
  primaryFg: 'hsl(var(--primary-foreground))',
};

function planIcon(id: string): LucideIcon {
  if (id === 'growth') return Rocket;
  if (id === 'business') return Crown;
  return Zap;
}

function planIdFromName(name: string): string {
  const key = name.toLowerCase();
  if (key.includes('growth')) return 'growth';
  if (key.includes('business') || key.includes('pro')) return 'business';
  if (key.includes('free')) return 'free';
  return 'plan';
}

function getAnalyticsLabel(plan: any): string {
  const raw = String(getAnalyticsValue(plan) ?? '').toLowerCase();
  if (raw.includes('advance')) return 'Advanced growth analytics';
  if (raw.includes('basic')) return 'Basic performance analytics';
  const formatted = formatPlanFeatureLabel(getAnalyticsValue(plan));
  return formatted === '—' ? 'Service analytics' : `${formatted} analytics`;
}

function formatFeaturedListings(raw: string | number): string {
  if (typeof raw === 'number') return `${raw}/month`;
  const s = String(raw || '0/month');
  return s.includes('/') ? s : `${s}/month`;
}

/** Map membership API plan → checkout UI model. */
export function mapApiPlanToCheckout(plan: any): CheckoutPlan {
  const featuredRaw =
    plan?.display?.limits?.featuredListings ?? getFeaturedListingsLimit(plan);
  const addons = (plan?.addons ?? []).map((a: any) => {
    const code = String(a.code || a.name || '').toLowerCase();
    const kind: 'team' | 'service' | 'other' = /team/.test(code)
      ? 'team'
      : /service/.test(code)
        ? 'service'
        : 'other';
    return {
      label: a.name || a.code || 'Add-on',
      kind,
    };
  });

  const name = plan?.name ?? 'Plan';
  const freeTrialDays =
    plan?.freeTrialEnabled && Number(plan?.freeTrialDays) > 0
      ? Number(plan.freeTrialDays)
      : undefined;

  return {
    tier: String(plan.tier),
    id: planIdFromName(name),
    name,
    tagline:
      PLAN_TAGLINES[name.toLowerCase()] ??
      'Scale your service business with the right tools.',
    monthly: Number(plan?.pricing?.monthly?.price ?? 0),
    yearly: Number(plan?.pricing?.yearly?.price ?? 0),
    freeTrialDays,
    limits: {
      services: Number(plan?.limits?.services ?? 0),
      teamMembers: Number(plan?.limits?.teamMembers ?? 0),
      featuredListings: formatFeaturedListings(featuredRaw),
    },
    analyticsLabel: getAnalyticsLabel(plan),
    messaging: Boolean(getMessagingValue(plan)),
    addons: addons.length > 0 ? addons : undefined,
  };
}

/**
 * Visual port of `checkout updated/src/components/CheckoutModal.tsx`.
 * Uses hsl(var(--token)) so color-mix / accents match that design on this app's HSL tokens.
 */
export default function CheckoutModal({
  open,
  onOpenChange,
  plan,
  billing,
  isLoading = false,
  onConfirm,
}: CheckoutModalProps) {
  if (!plan) return null;

  const isYearly = billing === 'yearly';
  const price = isYearly ? plan.yearly : plan.monthly;
  const dueToday = plan.freeTrialDays ? 0 : price;
  const period = isYearly ? 'year' : 'month';
  const billingLabel = isYearly ? 'Annual Billing' : 'Monthly Billing';
  const PlanIcon = planIcon(plan.id);

  const trialEndDate = plan.freeTrialDays
    ? new Date(Date.now() + plan.freeTrialDays * 24 * 60 * 60 * 1000)
    : null;
  const trialEndLabel = trialEndDate
    ? trialEndDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const included: { icon: LucideIcon; label: string }[] = [
    { icon: BarChart3, label: plan.analyticsLabel },
    {
      icon: Users,
      label: `Up to ${plan.limits.teamMembers} Team Member${
        plan.limits.teamMembers > 1 ? 's' : ''
      }`,
    },
    { icon: CalendarCheck, label: 'Booking Calendar' },
    {
      icon: Star,
      label: `${plan.limits.featuredListings.replace('/month', '')} Featured Listing${
        plan.limits.featuredListings.startsWith('1') ? '' : 's'
      } / month`,
    },
    {
      icon: Briefcase,
      label: `Up to ${plan.limits.services} Service Listings`,
    },
  ];

  if (plan.messaging) {
    included.push({ icon: MessageCircle, label: 'Messaging System' });
  }

  if (plan.addons && plan.addons.length > 0) {
    for (const addon of plan.addons) {
      const icon =
        addon.kind === 'team'
          ? UserPlus
          : addon.kind === 'service'
            ? Plus
            : Sparkles;
      included.push({ icon, label: addon.label });
    }
  }

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      size="5xl"
      scrollBehavior="inside"
      hideCloseButton
      isDismissable={!isLoading}
      backdrop="opaque"
      classNames={{
        wrapper:
          'fixed inset-0 items-center justify-center !overflow-hidden p-2 sm:p-3',
        backdrop: 'bg-black/80',
        base: 'max-w-[1040px] w-[calc(100vw-1rem)] max-h-[min(92vh,820px)] !my-0 bg-transparent shadow-none border-0 overflow-hidden',
        body: 'p-0 overflow-hidden',
      }}
    >
      <ModalContent className="p-0 gap-0 bg-transparent shadow-none border-0 max-h-[min(92vh,820px)] overflow-hidden">
        {(onClose) => (
          <div
            className="relative rounded-2xl shadow-2xl max-h-[min(92vh,820px)] overflow-y-auto overscroll-contain"
            style={{ background: C.card, color: C.foreground }}
          >
            <button
              type="button"
              aria-label="Close"
              disabled={isLoading}
              onClick={() => {
                onClose();
                onOpenChange(false);
              }}
              className="absolute right-3 top-3 z-20 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none"
              style={{ color: C.mutedFg }}
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative px-3 sm:px-5 lg:px-6 pt-4 pb-4">
              {/* Header */}
              <div className="flex items-start gap-2.5 mb-3 pr-7">
                <div
                  className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center"
                  style={{
                    background: `color-mix(in oklab, ${C.primary} 10%, transparent)`,
                  }}
                >
                  <ShoppingCart
                    className="h-4 w-4"
                    style={{ color: C.primary }}
                  />
                </div>
                <div>
                  <h2
                    className="text-base sm:text-lg font-bold tracking-tight leading-tight"
                    style={{ color: C.foreground }}
                  >
                    Checkout
                  </h2>
                  <p
                    className="mt-0.5 text-[11px] sm:text-xs"
                    style={{ color: C.mutedFg }}
                  >
                    Review your subscription before proceeding to payment
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-3 lg:gap-4">
                {/* LEFT: Plan overview */}
                <div className="flex flex-col gap-2.5 sm:gap-3">
                  <div
                    className="rounded-xl p-3 sm:p-3.5 border relative overflow-hidden"
                    style={{
                      borderColor: C.border,
                      background: `color-mix(in oklab, ${C.primary} 6%, ${C.background})`,
                    }}
                  >
                    <div className="flex flex-row items-start gap-3">
                      <div
                        className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-xl flex items-center justify-center"
                        style={{ background: 'var(--gradient-primary)' }}
                      >
                        <PlanIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span
                          className="inline-block text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full"
                          style={{
                            background: 'var(--gradient-primary)',
                            color: C.primaryFg,
                          }}
                        >
                          Selected Plan
                        </span>
                        <h3
                          className="mt-1 text-xl sm:text-2xl font-bold leading-tight"
                          style={{ color: C.foreground }}
                        >
                          {plan.name}
                        </h3>
                        <p
                          className="mt-0.5 text-xs line-clamp-2"
                          style={{ color: C.mutedFg }}
                        >
                          {plan.tagline}
                        </p>
                        <div
                          className="mt-1.5 flex items-center gap-1.5"
                          style={{ color: C.primary }}
                        >
                          <Tag className="h-3.5 w-3.5" />
                          <span className="text-xs font-semibold">
                            {billingLabel}
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-baseline gap-1">
                          <span
                            className="text-xl sm:text-2xl font-bold"
                            style={{ color: C.foreground }}
                          >
                            CAD {price.toFixed(2)}
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: C.mutedFg }}
                          >
                            / {period}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* What's Included */}
                  <div>
                    <h4
                      className="text-xs sm:text-sm font-bold mb-1.5"
                      style={{ color: C.foreground }}
                    >
                      What&apos;s Included
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {included.map((item, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5"
                          style={{
                            borderColor: C.border,
                            background: C.background,
                          }}
                        >
                          <span
                            className="h-4 w-4 shrink-0 rounded-full flex items-center justify-center"
                            style={{
                              background: `color-mix(in oklab, ${C.success} 15%, transparent)`,
                            }}
                          >
                            <Check
                              className="h-2.5 w-2.5"
                              style={{ color: C.success }}
                              strokeWidth={3}
                            />
                          </span>
                          <item.icon
                            className="h-3.5 w-3.5 shrink-0"
                            style={{ color: C.mutedFg }}
                          />
                          <span
                            className="text-[11px] sm:text-xs truncate"
                            style={{ color: C.foreground }}
                          >
                            {item.label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Trial banner */}
                  {trialEndLabel && (
                    <div
                      className="rounded-lg p-2.5 flex items-start gap-2.5 border"
                      style={{
                        background: `color-mix(in oklab, ${C.success} 10%, ${C.background})`,
                        borderColor: `color-mix(in oklab, ${C.success} 30%, transparent)`,
                      }}
                    >
                      <div
                        className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center"
                        style={{
                          background: `color-mix(in oklab, ${C.success} 20%, transparent)`,
                        }}
                      >
                        <Gift
                          className="h-4 w-4"
                          style={{ color: C.success }}
                        />
                      </div>
                      <div className="text-xs leading-snug">
                        <div className="font-bold" style={{ color: C.success }}>
                          Free for {Math.round(plan.freeTrialDays! / 30)}{' '}
                          months!
                        </div>
                        <div
                          style={{
                            color: `color-mix(in oklab, ${C.foreground} 80%, transparent)`,
                          }}
                        >
                          You won&apos;t be charged until {trialEndLabel}.
                          Cancel anytime before then.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Trust badges */}
                  <div className="grid grid-cols-3 gap-2 pt-0.5">
                    {[
                      {
                        icon: Shield,
                        title: 'Secure payment',
                        sub: 'Powered by Stripe',
                      },
                      {
                        icon: RefreshCw,
                        title: 'Cancel anytime',
                        sub: 'No hidden fees',
                      },
                      {
                        icon: ArrowUpDown,
                        title: 'Change plans anytime',
                        sub: 'Upgrade or downgrade',
                      },
                    ].map((t, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1.5 min-w-0"
                      >
                        <div
                          className="h-7 w-7 shrink-0 rounded-full flex items-center justify-center"
                          style={{
                            background: `color-mix(in oklab, ${C.primary} 10%, transparent)`,
                          }}
                        >
                          <t.icon
                            className="h-3.5 w-3.5"
                            style={{ color: C.primary }}
                          />
                        </div>
                        <div className="min-w-0">
                          <div
                            className="text-[10px] sm:text-xs font-semibold truncate"
                            style={{ color: C.foreground }}
                          >
                            {t.title}
                          </div>
                          <div
                            className="text-[9px] sm:text-[10px] truncate"
                            style={{ color: C.mutedFg }}
                          >
                            {t.sub}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT: Order summary */}
                <div
                  className="rounded-xl border p-3 sm:p-3.5 flex flex-col gap-2.5 h-fit"
                  style={{
                    borderColor: C.border,
                    background: C.background,
                  }}
                >
                  <div>
                    <h4
                      className="text-sm font-bold"
                      style={{ color: C.foreground }}
                    >
                      Order Summary
                    </h4>
                    <div className="mt-2 flex items-center justify-between text-xs sm:text-sm">
                      <span style={{ color: C.foreground }}>
                        {plan.name} ({isYearly ? 'Annual' : 'Monthly'})
                      </span>
                      <span
                        className="font-semibold"
                        style={{ color: C.foreground }}
                      >
                        CAD {price.toFixed(2)}
                      </span>
                    </div>
                    {plan.freeTrialDays ? (
                      <div className="flex items-center justify-between text-xs sm:text-sm mt-1.5">
                        <span style={{ color: C.success }}>
                          {Math.round(plan.freeTrialDays / 30)}-month free trial
                        </span>
                        <span
                          className="font-semibold"
                          style={{ color: C.success }}
                        >
                          - CAD {price.toFixed(2)}
                        </span>
                      </div>
                    ) : null}
                    <div
                      className="my-2 border-t"
                      style={{ borderColor: C.border }}
                    />
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm font-bold"
                        style={{ color: C.primary }}
                      >
                        Total Due Today
                      </span>
                      <span
                        className="text-base sm:text-lg font-bold"
                        style={{ color: C.primary }}
                      >
                        CAD {dueToday.toFixed(2)}
                      </span>
                    </div>
                    <div
                      className="mt-1 flex items-center gap-1 text-[10px] sm:text-xs"
                      style={{ color: C.mutedFg }}
                    >
                      <Lock className="h-3 w-3" />
                      All amounts are in CAD
                    </div>
                  </div>

                  <div
                    className="border-t pt-2.5"
                    style={{ borderColor: C.border }}
                  >
                    <h4
                      className="text-sm font-bold mb-2"
                      style={{ color: C.foreground }}
                    >
                      Subscription Details
                    </h4>
                    <ul className="space-y-1.5 text-xs">
                      {[
                        {
                          icon: ArrowUpDown,
                          label: 'Upgrade or downgrade your plan anytime',
                        },
                        {
                          icon: CalendarX,
                          label: 'Cancel your subscription at any time',
                        },
                        {
                          icon: Sparkles,
                          label:
                            'Your subscription begins immediately after payment',
                        },
                      ].map((d, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <d.icon
                            className="h-3.5 w-3.5 mt-0.5 shrink-0"
                            style={{ color: C.mutedFg }}
                          />
                          <span
                            style={{
                              color: `color-mix(in oklab, ${C.foreground} 90%, transparent)`,
                            }}
                          >
                            {d.label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className="rounded-lg p-2.5 flex items-start gap-2 border"
                    style={{
                      background: `color-mix(in oklab, ${C.primary} 6%, ${C.background})`,
                      borderColor: `color-mix(in oklab, ${C.primary} 20%, transparent)`,
                    }}
                  >
                    <Shield
                      className="h-4 w-4 shrink-0 mt-0.5"
                      style={{ color: C.primary }}
                    />
                    <div className="text-[11px] sm:text-xs leading-snug">
                      <div
                        className="font-bold"
                        style={{ color: C.foreground }}
                      >
                        Secure Checkout
                      </div>
                      <div style={{ color: C.mutedFg }}>
                        Your payment is securely processed by Stripe. We do not
                        store your payment information.
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={onConfirm}
                    className="w-full h-10 sm:h-11 text-xs sm:text-sm font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-80 disabled:pointer-events-none transition-opacity shrink-0"
                    style={{
                      background: 'var(--gradient-popular)',
                      color: C.primaryFg,
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Lock className="h-3.5 w-3.5" />
                    )}
                    {isLoading
                      ? 'Processing...'
                      : 'Continue to Secure Checkout'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
